import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

/**
 * Badge — Secure Labs Design System (UIP-04)
 *
 * Status variants follow DS-10 exactly: radius-md (6px), text-xs (11px),
 * Inter SemiBold, tracking-wide (+0.05em), ALL CAPS, tinted background.
 *
 * Generic variants (default, secondary, outline, ghost) kept for non-status use.
 */
const badgeVariants = cva(
  [
    'group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden',
    'whitespace-nowrap transition-all',
    'focus-visible:ring-2 focus-visible:ring-[var(--text-accent)] focus-visible:ring-offset-1',
    '[&>svg]:pointer-events-none [&>svg]:size-3.5',
  ].join(' '),
  {
    variants: {
      variant: {
        // ── Status variants (UIP-04.1) ──────────────────────────────────────
        // All status badges: radius-md (6px), text-xs, semibold, uppercase, tracking-wide
        granted:
          'rounded-[var(--radius-md)] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide bg-[var(--status-green-tint)] text-[var(--status-green-text)]',
        denied:
          'rounded-[var(--radius-md)] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide bg-[var(--status-red-tint)] text-[var(--status-red-text)]',
        visitor:
          'rounded-[var(--radius-md)] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide bg-[var(--status-blue-tint)] text-[var(--status-blue-text)]',
        pending:
          'rounded-[var(--radius-md)] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide bg-[var(--status-purple-tint)] text-[var(--status-purple-text)]',
        warning:
          'rounded-[var(--radius-md)] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide bg-[var(--status-amber-tint)] text-[var(--status-amber-text)]',
        expired:
          'rounded-[var(--radius-md)] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide bg-[rgba(107,90,72,0.20)] text-[var(--ink-muted)]',
        active:
          'rounded-[var(--radius-md)] px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide bg-[var(--status-green-tint)] text-[var(--status-green-text)]',

        // ── Generic variants (shadcn compat) ────────────────────────────────
        default:
          'rounded-full h-5 px-2 py-0.5 text-xs font-medium border border-transparent bg-[var(--cta-primary-bg)] text-[var(--cta-primary-text)]',
        secondary:
          'rounded-full h-5 px-2 py-0.5 text-xs font-medium border border-transparent bg-[var(--bg-surface-raised)] text-[var(--text-secondary)]',
        destructive:
          'rounded-full h-5 px-2 py-0.5 text-xs font-medium border border-transparent bg-[var(--status-red-tint)] text-[var(--status-red-text)]',
        outline:
          'rounded-full h-5 px-2 py-0.5 text-xs font-medium border border-[var(--border-default)] text-[var(--text-primary)]',
        ghost:
          'rounded-full h-5 px-2 py-0.5 text-xs font-medium border border-transparent hover:bg-[rgba(245,238,222,0.07)] text-[var(--text-secondary)]',
        link: 'text-xs font-medium text-[var(--text-accent)] underline-offset-4 hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants };
